import React, { useState, useEffect } from 'react';
import API from '../lib/api';
import { FlaskConical, Play, BarChart3, TrendingUp, Zap, Target, Repeat, Star } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import {
    BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer,
    AreaChart, Area, CartesianGrid, RadarChart, Radar, PolarGrid, PolarAngleAxis, PolarRadiusAxis
} from 'recharts';
import EnginePredictionBanner from '../components/dashboard/EnginePredictionBanner';

export default function SimulationLab() {
    const [fixtures, setFixtures] = useState([]);
    const [selectedMatch, setSelectedMatch] = useState('');
    const [numSims, setNumSims] = useState('10000');
    const [simulation, setSimulation] = useState(null);
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        API.get('/fixtures').then(res => setFixtures(res.data.fixtures || []));
    }, []);

    const runSimulation = async () => {
        if (!selectedMatch) return;
        setLoading(true);
        try {
            const res = await API.get(`/simulation/${selectedMatch}?num_sims=${numSims}`);
            setSimulation(res.data);
        } catch (err) {
            console.error(err);
        }
        setLoading(false);
    };

    const s = simulation;

    return (
        <div className="space-y-6" data-testid="simulation-lab">
            <EnginePredictionBanner engineKey="monte_carlo" matchId={selectedMatch} />
            <div className="glass-card p-6">
                <div className="flex items-center gap-3 mb-4">
                    <FlaskConical className="w-5 h-5 text-purple-400" />
                    <h2 className="heading-primary text-base md:text-xl text-white">MONTE CARLO SIMULATION ENGINE</h2>
                </div>
                <p className="text-sm text-white/40 mb-4">Run thousands of match simulations using batting averages, bowling economy, run rate trends, player form, venue conditions, H2H records, and toss scenarios.</p>
                <div className="flex flex-col sm:flex-row gap-4 items-start sm:items-end">
                    <div className="flex-1 w-full max-w-md">
                        <Select value={selectedMatch} onValueChange={setSelectedMatch}>
                            <SelectTrigger data-testid="sim-match-selector" className="bg-black/50 border-white/10 text-white h-12">
                                <SelectValue placeholder="Select match..." />
                            </SelectTrigger>
                            <SelectContent className="bg-[#0a0a0a] border-white/10">
                                {fixtures.filter(f => f.status !== 'completed').map(f => (
                                    <SelectItem key={f.id} value={f.id} className="text-white hover:bg-white/10">
                                        {f.home_team?.short_name} vs {f.away_team?.short_name} - {f.date?.slice(0, 10)} - {f.venue?.city}
                                    </SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                    </div>
                    <div className="w-full max-w-[140px]">
                        <Select value={numSims} onValueChange={setNumSims}>
                            <SelectTrigger className="bg-black/50 border-white/10 text-white h-12">
                                <SelectValue />
                            </SelectTrigger>
                            <SelectContent className="bg-[#0a0a0a] border-white/10">
                                {['1000', '5000', '10000', '25000', '50000'].map(n => (
                                    <SelectItem key={n} value={n} className="text-white hover:bg-white/10">{Number(n).toLocaleString()} sims</SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                    </div>
                    <Button data-testid="run-simulation-btn" onClick={runSimulation} disabled={loading || !selectedMatch}
                        className="h-12 bg-purple-500 text-white font-bold uppercase tracking-wide hover:bg-purple-400 shadow-[0_0_20px_rgba(168,85,247,0.3)]">
                        {loading ? 'Simulating...' : <><Play className="w-4 h-4 mr-2" />Run Simulation</>}
                    </Button>
                </div>
            </div>

            {s && (
                <>
                    <div className="grid grid-cols-1 lg:grid-cols-12 gap-4">
                        {/* Win Probability */}
                        <div className="lg:col-span-4 glass-card p-6" data-testid="sim-result">
                            <h3 className="heading-primary text-sm text-white mb-4">SIMULATION RESULT</h3>
                            <div className="space-y-6">
                                <div>
                                    <div className="flex justify-between mb-2">
                                        <span className="tech-mono text-xs text-cyan-400">{s.team_a?.short_name}</span>
                                        <span className="heading-primary text-2xl neon-text">{s.team_a_win_pct}%</span>
                                    </div>
                                    <div className="prob-bar"><div className="prob-bar-fill bg-cyan-500" style={{ width: `${s.team_a_win_pct}%` }} /></div>
                                </div>
                                <div>
                                    <div className="flex justify-between mb-2">
                                        <span className="tech-mono text-xs text-pink-400">{s.team_b?.short_name}</span>
                                        <span className="heading-primary text-2xl neon-text-pink">{s.team_b_win_pct}%</span>
                                    </div>
                                    <div className="prob-bar"><div className="prob-bar-fill bg-pink-500" style={{ width: `${s.team_b_win_pct}%` }} /></div>
                                </div>
                                <div className="text-center pt-4 border-t border-white/5">
                                    <div className="tech-mono text-[10px] text-white/30">TIE PROBABILITY</div>
                                    <div className="heading-primary text-xl text-yellow-400">{s.tie_pct}%</div>
                                </div>
                                <div className="text-center text-[10px] text-white/20 tech-mono">
                                    {s.simulations?.toLocaleString()} SIMULATIONS | CONFIDENCE: {(s.confidence * 100).toFixed(0)}%
                                </div>
                            </div>
                        </div>

                        {/* Score Distribution */}
                        <div className="lg:col-span-8 glass-card p-6" data-testid="sim-distribution">
                            <div className="flex items-center gap-2 mb-4">
                                <BarChart3 className="w-4 h-4 text-green-400" />
                                <h3 className="heading-primary text-sm text-white">SCORE DISTRIBUTION</h3>
                            </div>
                            <ResponsiveContainer width="100%" height={280}>
                                <AreaChart data={s.distribution || []}>
                                    <CartesianGrid strokeDasharray="3 3" stroke="#222" />
                                    <XAxis dataKey="range" tick={{ fill: '#666', fontSize: 10 }} interval={2} />
                                    <YAxis tick={{ fill: '#666', fontSize: 10 }} />
                                    <Tooltip contentStyle={{ background: '#000', border: '1px solid #333', borderRadius: 8 }} labelStyle={{ color: '#fff' }} />
                                    <Area type="monotone" dataKey="team_a" stroke="#00E5FF" fill="#00E5FF" fillOpacity={0.2} name={s.team_a?.short_name} />
                                    <Area type="monotone" dataKey="team_b" stroke="#FF2D95" fill="#FF2D95" fillOpacity={0.2} name={s.team_b?.short_name} />
                                </AreaChart>
                            </ResponsiveContainer>
                        </div>
                    </div>

                    {/* Stats Row */}
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                        <div className="glass-card p-4 text-center">
                            <div className="tech-mono text-[10px] text-white/30">AVG SCORE {s.team_a?.short_name}</div>
                            <div className="heading-primary text-3xl text-cyan-400">{s.team_a_avg_score}</div>
                            <div className="text-[10px] text-white/20">{s.team_a_score_range}</div>
                        </div>
                        <div className="glass-card p-4 text-center">
                            <div className="tech-mono text-[10px] text-white/30">AVG SCORE {s.team_b?.short_name}</div>
                            <div className="heading-primary text-3xl text-pink-400">{s.team_b_avg_score}</div>
                            <div className="text-[10px] text-white/20">{s.team_b_score_range}</div>
                        </div>
                        <div className="glass-card p-4 text-center">
                            <div className="tech-mono text-[10px] text-white/30">H2H RECORD</div>
                            <div className="heading-primary text-xl text-white">
                                {s.h2h ? `${s.h2h.team_a?.wins}-${s.h2h.team_b?.wins}` : 'N/A'}
                            </div>
                            <div className="text-[10px] text-white/20">{s.h2h?.total_matches || 0} matches</div>
                        </div>
                        <div className="glass-card p-4 text-center">
                            <div className="tech-mono text-[10px] text-white/30">VENUE AVG</div>
                            <div className="heading-primary text-xl text-yellow-400">{s.venue_stats?.avg_score || '-'}</div>
                            <div className="text-[10px] text-white/20">{s.venue_stats?.venue_total_matches || 0} matches</div>
                        </div>
                    </div>

                    {/* Toss Scenarios & Key Factors */}
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                        {/* Toss Scenarios */}
                        {s.toss_scenarios && (
                            <div className="glass-card p-6" data-testid="toss-scenarios">
                                <div className="flex items-center gap-2 mb-4">
                                    <Repeat className="w-4 h-4 text-yellow-400" />
                                    <h3 className="heading-primary text-sm text-white">TOSS SCENARIO ANALYSIS</h3>
                                </div>
                                <div className="space-y-4">
                                    <div className="p-4 bg-white/5 rounded-lg">
                                        <div className="text-xs text-white/40 mb-2">{s.team_a?.short_name} bats first</div>
                                        <div className="flex justify-between">
                                            <span className="text-sm text-cyan-400">{s.team_a?.short_name}: {s.toss_scenarios.team_a_bats_first?.team_a_win_pct}%</span>
                                            <span className="text-sm text-pink-400">{s.team_b?.short_name}: {s.toss_scenarios.team_a_bats_first?.team_b_win_pct}%</span>
                                        </div>
                                        <div className="prob-bar h-2 mt-2">
                                            <div className="h-full rounded-full bg-cyan-500" style={{ width: `${s.toss_scenarios.team_a_bats_first?.team_a_win_pct}%` }} />
                                        </div>
                                    </div>
                                    <div className="p-4 bg-white/5 rounded-lg">
                                        <div className="text-xs text-white/40 mb-2">{s.team_b?.short_name} bats first</div>
                                        <div className="flex justify-between">
                                            <span className="text-sm text-cyan-400">{s.team_a?.short_name}: {s.toss_scenarios.team_b_bats_first?.team_a_win_pct}%</span>
                                            <span className="text-sm text-pink-400">{s.team_b?.short_name}: {s.toss_scenarios.team_b_bats_first?.team_b_win_pct}%</span>
                                        </div>
                                        <div className="prob-bar h-2 mt-2">
                                            <div className="h-full rounded-full bg-cyan-500" style={{ width: `${s.toss_scenarios.team_b_bats_first?.team_a_win_pct}%` }} />
                                        </div>
                                    </div>
                                </div>
                            </div>
                        )}

                        {/* Key Factors Radar */}
                        {s.key_factors && (
                            <div className="glass-card p-6" data-testid="key-factors">
                                <div className="flex items-center gap-2 mb-4">
                                    <Target className="w-4 h-4 text-green-400" />
                                    <h3 className="heading-primary text-sm text-white">KEY FACTOR COMPARISON</h3>
                                </div>
                                <div className="space-y-3">
                                    {s.key_factors.map((kf, i) => (
                                        <div key={i} className="p-3 bg-white/5 rounded-lg">
                                            <div className="flex items-center justify-between mb-2">
                                                <span className="text-xs text-white/60">{kf.factor}</span>
                                                <span className="tech-mono text-[10px] text-yellow-400">{kf.advantage}</span>
                                            </div>
                                            <div className="flex items-center gap-3">
                                                <span className="text-xs text-cyan-400 w-12">{kf.team_a}%</span>
                                                <div className="flex-1 h-2 bg-white/5 rounded-full overflow-hidden flex">
                                                    <div className="h-full bg-cyan-500 rounded-l-full" style={{ width: `${kf.team_a / (kf.team_a + kf.team_b) * 100}%` }} />
                                                    <div className="h-full bg-pink-500 rounded-r-full flex-1" />
                                                </div>
                                                <span className="text-xs text-pink-400 w-12 text-right">{kf.team_b}%</span>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                                {/* Form & Venue */}
                                {s.form && (
                                    <div className="mt-4 pt-4 border-t border-white/5 grid grid-cols-2 gap-3">
                                        <div className="p-2 bg-white/5 rounded text-center">
                                            <div className="text-[10px] text-white/30">FORM {s.team_a?.short_name}</div>
                                            <div className="text-sm text-cyan-400">{s.form.team_a?.wins}W-{s.form.team_a?.losses}L</div>
                                        </div>
                                        <div className="p-2 bg-white/5 rounded text-center">
                                            <div className="text-[10px] text-white/30">FORM {s.team_b?.short_name}</div>
                                            <div className="text-sm text-pink-400">{s.form.team_b?.wins}W-{s.form.team_b?.losses}L</div>
                                        </div>
                                    </div>
                                )}
                            </div>
                        )}
                    </div>

                    {/* Impact Player Substitution Scenarios */}
                    {s.impact_player_scenarios && (
                        <div className="glass-card p-6" data-testid="sim-impact-scenarios">
                            <div className="flex items-center gap-2 mb-4">
                                <Star className="w-4 h-4 text-orange-400" />
                                <h3 className="heading-primary text-sm text-white">IMPACT PLAYER SUBSTITUTION SCENARIOS</h3>
                                <span className="text-[10px] text-orange-400/60 tech-mono ml-auto">MAX SWING: {s.impact_player_scenarios.max_swing}%</span>
                            </div>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                {[
                                    { team: s.team_a, scenarios: s.impact_player_scenarios.team_a_scenarios, best: s.impact_player_scenarios.best_impact_choice?.team_a },
                                    { team: s.team_b, scenarios: s.impact_player_scenarios.team_b_scenarios, best: s.impact_player_scenarios.best_impact_choice?.team_b },
                                ].map((side, si) => (
                                    <div key={si}>
                                        <div className="text-xs font-bold mb-2" style={{ color: side.team?.color }}>{side.team?.short_name} SCENARIOS</div>
                                        {side.best && (
                                            <div className="p-3 bg-orange-500/5 border border-orange-500/10 rounded-lg mb-2 flex items-center justify-between">
                                                <div>
                                                    <div className="text-xs text-white/80 font-bold">{side.best.player_name}</div>
                                                    <div className="text-[9px] text-white/30">{side.best.impact_role} impact | BEST CHOICE</div>
                                                </div>
                                                <div className={`heading-primary text-lg ${side.best.delta > 0 ? 'text-green-400' : 'text-red-400'}`}>
                                                    {side.best.delta > 0 ? '+' : ''}{side.best.delta}%
                                                </div>
                                            </div>
                                        )}
                                        <div className="space-y-1">
                                            {side.scenarios?.slice(0, 4).map((sc, i) => (
                                                <div key={i} className="flex items-center justify-between text-xs p-1.5 bg-white/5 rounded">
                                                    <span className="text-white/60">{sc.player_name} ({sc.impact_role})</span>
                                                    <span className={sc.delta > 0 ? 'text-green-400' : sc.delta < 0 ? 'text-red-400' : 'text-white/40'}>
                                                        {sc.delta > 0 ? '+' : ''}{sc.delta}%
                                                    </span>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}
                </>
            )}
        </div>
    );
}
